/**
 * load core modules
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import axios from "axios";
/* load core modules end */

/**
 * load actions
 */
import {
  showModalPopup,
  showPlaybackError,
  showToastNotification,
  loadChannels,
  resetAction,
} from "../../App/state/actions";
import {
  loadProgramDetail,
  loadRecordingProgramDetail,
  loadCurrentSeasonDetails,
  loadCurrentSeasonEntitlements,
} from "./state/actions";
/* load actions end */

/**
 * load constants
 */
import constants from "../../shared/constants";
import recordingConstants from "../../shared/constants/recordingConstants";
import PlayerConstants from "../../shared/constants/player";
import parentalControlConstants from "../../shared/constants/parentalControls";
import { ANALYTICS_EVENT_TYPES, MAPPED_CONTENT_TYPES } from "../../shared/constants/analytics";
/* load constants end */

/**
 * load util functions
 */
import { createEpgChannelObject, isPPVChannel, isUserSubscribedChannel } from "../../shared/utils/epg";
import { getRecordingSystemType } from "../../shared/utils/recordingHelper";
import useGuidePrograms, { getSegmentTimestamp, getNextSegmentTimestamp } from "../../shared/hooks/useGuidePrograms";
import { getCustomContextMetadata, trackConvivaCustomEvent } from "../../shared/analytics/media";
/* load util functions end */

/**
 * load analytics functions
 */
import { trackMediaEnd } from "../../shared/analytics/media";
import { trackGenericAction } from "../../shared/analytics/dataLayer";
import { logSectionOpened } from "../../shared/analytics/dynatrace";
import useTrackPageView from "../../shared/hooks/useTrackPageView";
import { DT_SECTIONS } from "../../shared/constants/dynatrace.js";
/* load analytics functions end */

/**
 * load components
 */
import LivePlayer from "../../components/VideoPlayer/LivePlayer";
import VodPlayer from "../../components/VideoPlayer/VodPlayer";
import RecordingPlayer from "../../components/VideoPlayer/RecordingPlayer";
import VideoPlayerSideBar from "../../components/VideoPlayerSideBar";
import ContentLockedOverlay from "../../components/ContentLockedOverlay";
import SeoPageTags from "../../components/SeoPageTags";
import VodPlayerSideBar from "../../components/VodPlayerSideBar";
import MovieSideBar from "../../components/MovieSideBar";
/* load components end */

/**
 * load assets
 */
import { getAVSKeyArtImage } from "../../shared/utils/image";
import "./style.scss";
const outHomeIcon = process.env.PUBLIC_URL + "/images/Out_of_home.svg";
/* load assets end */

/** declare/destructure constants */
const { IMAGES, MODAL_TYPES, APP_ENVIRONMENTS, SPORT } = constants;
const ENV = process.env.NODE_ENV || APP_ENVIRONMENTS.PROD;
const { RECORDING_PACKAGES } = recordingConstants;
const {
  PLAYER_TYPE: { VOD, LIVE, RECORDING },
  PLAYBACK_TYPES,
  NON_FATAL_ERROR_CODES: nonFatalErrorCodes,
  PLAYER_ERROR_CODES: playerErrorCodes,
} = PlayerConstants;
const { PLAYER_PIN_REFRESH_INTERVAL_MS } = parentalControlConstants;

/** load page helpers */
import {
  getLivePlayerMetaData,
  getLookbackMetaData,
  getVodPlayerMetaData,
  getRecordingPlayerMetaData,
} from "./helpers/mediaNodeMetadataHelpers";
import {
  resetIsLoading,
  onFullScreenClick,
  handlePlaybackStateChange,
  updateSeasonEntitlements,
  resetPlayerPage,
} from "./helpers/playerHelpers";
import { setVideoStreamAttributes, resetPlayerAttributes, getDTContentType } from "./helpers/dynatraceHelpers";
import { triggerAnalyticsCalls } from "./helpers/analyticsHelpers";
import { transitionProgram, changeLookbackStream, exitLookback } from "./helpers/socuHelpers";
import { handleHeartBeat } from "./helpers/heartBeatHelpers";
import { fetchStream, handleStreamChange } from "./helpers/streamHelpers";
import { updatePrevLiveProgramDetails } from "./helpers/liveStreamHelpers";
import {
  updateStreamReadyToChangeState,
  toastContentOOHBlocked,
  handleChannelChange,
} from "./helpers/liveSidebarHelpers";
import { updateUrl, setAnalyticsLink, loadProgramDetails, handleProgramChange } from "./helpers/staticSidebarHelpers";
import { createPinModal, reportParentalPinStart } from "./helpers/parentalControlHelpers"; // Adjust the import path as necessary

/** load modules */
import usePlaybackParams from "./modules/playbackParams";
import usePlayerStreamInfo from "./modules/playerStreamInfo";
import usePlayerUnmount from "./modules/playerUnmount";
import useContentMetadata from "./modules/contentMetadata";
import useRecordingToast from "./modules/recordingToast";
import useRecordingActions from "./modules/recordingActions";
import useRecordingEffects from "./modules/recordingEffects";
import useRecordingSidebar from "./modules/recordingSidebar";
import useLiveSidebar from "./modules/liveSidebar";
import { useStaticPlayerEffects } from "./modules/staticPlayerEffects";
import { useLivePlayerEffects } from "./modules/livePlayerEffects";
import { useLiveChannelTune } from "./modules/liveChannelTune";
import { useSOCUEffects } from "./modules/socuEffects";
import { setTeamRecordingAction, SET_TEAM_RECORDINGS } from "../RecordingsPage/state/actions";
import TeamsRecordingInstancesModal from "../../components/TeamsRecordingInstancesModal";

/**
 * Creates a page that streams videos
 *
 * @component
 * @param {*} props - Player props
 */
function PlayerPage() {
  const appProvider = useSelector(({ app }) => app.provider);
  const userProfile = useSelector(({ app }) => app.userProfile);
  const isInHome = useSelector(({ app }) => app.isInHome);
  const playbackError = useSelector(({ app }) => app.playbackError);
  const subscribedChannels = useSelector(({ app }) => app.subscribedChannels);
  const channelMapInfo = useSelector(({ app }) => app.channelMapInfo);
  const featureToggles = useSelector(({ app }) => app.featureToggles);
  const convivaContentSubType = useSelector(({ app }) => app.convivaContentSubType);
  const seasonFeedContent = useSelector(({ onDemandPlayer }) => onDemandPlayer.seasonContent);
  const isRented = useSelector(({ onDemandPlayer }) => onDemandPlayer.isRented);
  const onDemandProgramContent = useSelector(({ onDemandPlayer }) => onDemandPlayer?.programContent);
  const recordingProgramContent = useSelector(({ recordingPlayer }) => recordingPlayer?.programContent);
  const sportEventRecordingDataObj = useSelector(({ recordingPlayer }) => recordingPlayer?.sportEventRecordingData);
  const programContent = onDemandProgramContent || recordingProgramContent;

  const dispatch = useDispatch();

  const {
    isChannelTuneTimeEnabled: isCTTEnabled,
    isParentalPINEnabled: isPcPinFeatureEnabled,
    isRecordingEnabled,
    isPPVFeatureEnabled: isPPVEnabled,
    isUserProfilesEnabled,
    isConvivaAppTrackerEnabled,
    isVODDisabled,
    isLIVEDisabled,
    isFASTDisabled,
    isRestartDisabled,
    isCatchUpDisabled,
    isCPVRDisabled,
    isCPVRBookmarksEnabled,
    isDynatraceTrackingEnabled,
    isTeamRecordingsEnabled,
  } = featureToggles;

  /** Player modules hook */
  const {
    history,
    playbackType,
    playbackID,
    externalId,
    channelID,
    contentType,
    isLookback,
    playbackContent,
    playbackVideoUrl,
    playbackAuthToken,
    playbackBookmark,
    playbackSkipTimeSeconds,
    playbackAssetId,
    playbackPcPin,
    isLivePlayer,
    isVodPlayer,
    isRecordingPlayer,
    stopDeltaTime,
  } = usePlaybackParams();

  const { t: translate } = useTranslation();
  const { trackPageView, resetIsPageViewTracked } = useTrackPageView();

  /** declare states */
  const [streamInfo, setStreamInfo] = useState(null);
  const [metadataInfo, setMetadataInfo] = useState(null);
  // Currently using it only if the asset is conflicted.
  const [showRecordingModal, setShowRecordingModal] = useState(false);
  const [showSideNav, setShowSideNav] = useState(false);
  const [currentChannelId, setCurrentChannelId] = useState(channelID);
  const [currentEpisodeId, setCurrentEpisodeId] = useState(playbackID);
  const [liveProgramDetails, setLiveProgramDetails] = useState(null);
  const [miniGuideOnNowPrograms, setMiniGuideOnNowPrograms] = useState(null);
  const [miniGuideOnNextPrograms, setMiniGuideOnNextPrograms] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isInFullScreen, setIsInFullScreen] = useState(true);
  const [authToken, setAuthToken] = useState(playbackAuthToken || null);
  const [videoUrl, setVideoUrl] = useState(playbackVideoUrl || null);
  const [assetId, setAssetId] = useState(playbackAssetId || null);
  const [fatalPlaybackError, setFatalPlaybackError] = useState(null);
  const [playerControlState, setPlayerControlState] = useState(null);
  const [showStillWatchingPrompt, setShowStillWatchingPrompt] = useState(false);
  const [selectedAssetRecordingInfo, setSelectedAssetRecordingInfo] = useState(null);
  const [recordingActionError, setRecordingActionError] = useState(null);
  // isReadyToChangeStreams acts as a lock that blocks fetching a new stream until the current stop content call has completed.
  const [isReadyToChangeStreams, setIsReadyToChangeStreams] = useState(true);
  const [isParentalPinEnabled, setIsParentalPinEnabled] = useState(false);
  // isCallParentalPinLock is used as a dependency within a useEffect to call parental_pin_content_event once programContent is ready
  const [isCallParentalPinLock, setIsCallParentalPinLock] = useState(false);
  const [showContentLockedOverlay, setShowContentLockedOverlay] = useState(false);
  const [bingeWatchEpisodeCounter, setBingeWatchEpisodeCounter] = useState(0);
  const [isPinLocked, setIsPinLocked] = useState(false);
  const [pageTitle, setPageTitle] = useState("");
  const [currentProgram, setCurrentProgram] = useState(null);
  const [bookmark, setBookmark] = useState(playbackBookmark || null);
  const [vodProgress, setVodProgress] = useState(0);
  const [hasChannelTuneInfo, setHasChannelTuneInfo] = useState(false);
  const [progressTime, setProgressTime] = useState(playbackSkipTimeSeconds || null);
  const [livePlaybackCanStart, setLivePlaybackCanStart] = useState(
    playbackVideoUrl && playbackAuthToken ? true : false
  );
  const [PCBlocked, setPCBlocked] = useState(false);
  const [restartLiveStream, setRestartLiveStream] = useState(false);
  const [isLookbackStream, setIsLookbackStream] = useState(isLookback); // flag to indicate a lookback stream
  const [updateLookbackStream, setUpdateLookbackStream] = useState(false); // flag to indicate update of stream on same channel when a lookback stream ends
  const [ppvPurchasePackage, setPpvPurchasePackage] = useState(playbackContent?.purchasePackage);
  const initPlaybackTimestamp = useRef(moment().valueOf()); // timestamp when user initiates playback default to player page load time
  const [triggerSafariUpdate, setTriggerSafariUpdate] = useState(false);
  const [teamRecordingData, setTeamRecordingData] = useState(null);
  const [showRecordingInstancesModal, setShowRecordingInstancesModal] = useState(false);
  const isUnrestrictedPlay = useRef(history?.location?.state?.playbackContent?.unrestrictedPlay);

  /** declare refs */
  const isTVShow = useRef(null);
  const buttonRef = useRef(null);
  // isLoading prevents changing episodes in the sidebar while a fetchVideoUrl call is being made.
  const isLoading = useRef(false);
  const pcPin = useRef(playbackPcPin || null);
  const pcPinTimer = useRef(null);
  const isProgramChangedRef = useRef(false);
  // isLoading prevents changing channels in the sidebar while a fetchVideoUrl call is being made.
  const currentAssetId = useRef(null);
  const isRecordingActionInProgress = useRef(false);
  const onSwitchProgramEndSessionRef = useRef(null);
  const isSwitchChannelRef = useRef(false);
  const isInitMediaSessionTriggeredRef = useRef(false);
  const toastNotificationTimer = useRef(null); // Timer ref for unmount clean up
  const isInitMediaSessionTriggeredTimer = useRef(null); // Timer ref for unmount clean up
  // We need to store the value of liveProgramDetails/programContent in a ref to pass into the tracking for pin authorize complete,
  // but liveProgramDetails itself cannot be a ref because we depend on its value changing in an effect
  const programDetailsRef = useRef(isLivePlayer ? liveProgramDetails : programContent);
  const cancelTokenSource = useRef(axios.CancelToken.source()); // cancelTokenSource ref for requests unmount clean up
  const prevContentIdForApiRef = useRef(null); // Persist previous contentId to prevent additional middleware API calls in update.
  // TCDWC-2189 TODO: create one mechanism for triggering playback retuning that supports all of the different scenarios (restart, lookback, PPV, etc.)
  const prevPurchasePackageForApiRef = useRef(ppvPurchasePackage); // Persist previous purchase package to know when to retrigger the tuning logic after a purchase (since channel & program IDs remain the same)
  const prevAuthToken = useRef(null); // Persist previous authToken to prevent additional streamInfo set. Additional streamInfo set will trigger unnecessary player source load.
  const userInactivityTimer = useRef(null);
  const isRestartedStream = useRef(false); // boolean ref to act as check if current stream is a start over stream
  const prevVideoUrl = useRef(null); // Persist previous video url to prevent duplicate video stream creation
  const isNextProgramNonRestartable = useRef(false); // boolean ref to determine if next live program should start over automatically or not
  const restartAssetId = useRef(null); // Persist asset id of start over stream
  const preventReadyToChangeStreams = useRef(false);
  const useRoutePlaybackState = useRef(playbackContent ? true : false); // boolean ref to act as check for using the route state for loading playback
  const exitLookbackMode = useRef(false); // flag to exit the lookback stream
  const lookbackContent = useRef(playbackContent?.lookbackContent); // ref to persist and update the lookback content
  const didSeasonChange = useRef(false);
  // liveProgramCanUpdate acts as a liveProgramDetails lock. It's useful when a user pauses live playback. In such scenario, we don't need premature liveProgramDetails update
  const liveProgramCanUpdate = useRef(true);
  const dtContentType = getDTContentType(contentType, isLookbackStream, isRestartedStream);
  const currentPlayingSource = useRef(null);
  const haltTransitionSOCU = useRef(false);
  const hasReportedDT = useRef(false);
  const sportEventRecordingData = sportEventRecordingDataObj && sportEventRecordingDataObj[playbackID];
  const { isTeamRecording, isSportRecording } = sportEventRecordingData || {};

  const redirectUri =
    ENV === APP_ENVIRONMENTS.DEV ? window.location.origin : appProvider.config.general.AVS_config.redirectUri;

  const { loadGuidePrograms } = useGuidePrograms();

  const recordingSystemType = useMemo(
    () => (userProfile?.isLoggedIn ? getRecordingSystemType(userProfile) : null),
    [userProfile]
  );
  const isPlayerControlsDisable = useMemo(() => {
    if (appProvider.panicMode && isLivePlayer) return true;
  }, [appProvider.panicMode, isLivePlayer]);

  /** isMR - type: boolean - check if a login user has Media Room recording package, this constant is crucial for recording functions*/
  const isMR = recordingSystemType === RECORDING_PACKAGES.PACKAGE_NAME.LPVRMediaroom_TP;

  const contentSubtype = useMemo(
    () =>
      programContent && programContent[0] && programContent[0].metadata && programContent[0].metadata.contentSubtype,
    [programContent]
  );

  if (!hasReportedDT.current) {
    hasReportedDT.current = true;
    logSectionOpened(DT_SECTIONS.PLAYER, null, isDynatraceTrackingEnabled);
  }

  const isSportEvent = (recordingInfo) => {
    const layoutHint = recordingInfo?.assetToRecord?.metadata?.extendedMetadata?.dlum?.layoutHint || null;
    if (layoutHint === SPORT) {
      return true;
    }
    return false;
  };

  if (contentSubtype !== "MOVIE") {
    isTVShow.current = true;
  } else {
    isTVShow.current = false;
  }

  const getMediaNodeMetaData = useCallback(() => {
    let metaData = getLivePlayerMetaData(programDetailsRef, playbackType, isRestartedStream);
    if (metaData) return metaData;

    metaData = getLookbackMetaData(lookbackContent, playbackType);
    if (metaData) return metaData;

    metaData = getVodPlayerMetaData(programDetailsRef, convivaContentSubType, playbackType);
    if (metaData) return metaData;

    metaData = getRecordingPlayerMetaData(programDetailsRef, playbackType, programContent, isMR);
    if (metaData) return metaData;

    return {};
  }, [convivaContentSubType, programContent, isMR, playbackType]);

  // Channel item that is being tuned to
  const currentChannel = useMemo(() => {
    if (isLivePlayer && channelMapInfo) {
      const channel = channelMapInfo.containers?.find((channel) => channel?.id === currentChannelId);
      if (channel) return createEpgChannelObject(channel, appProvider.channelMapId);
    }

    return null;
  }, [appProvider, channelMapInfo, currentChannelId, isLivePlayer]);

  const isPPV = isPPVChannel(currentChannel);
  if (
    !isUnrestrictedPlay &&
    isLivePlayer &&
    currentChannel &&
    ((isPPV && !isPPVEnabled) || !isUserSubscribedChannel(currentChannel, subscribedChannels))
  ) {
    // If user has bookmarked the player page in a state that is no longer supported, kick them out
    history.replace("/");
  }
  const restartUserInactivityTimer = useCallback(() => {
    if (userInactivityTimer.current) {
      clearTimeout(userInactivityTimer.current);
    }
    setShowStillWatchingPrompt(false);
    if (isLivePlayer) {
      userInactivityTimer.current = setTimeout(() => {
        setShowStillWatchingPrompt(true);
      }, appProvider.config.player_inactivity.live_timeout * 1000);
    } else if (isVodPlayer || isRecordingPlayer) setBingeWatchEpisodeCounter(0);
  }, [
    isLivePlayer,
    isVodPlayer,
    isRecordingPlayer,
    setBingeWatchEpisodeCounter,
    appProvider.config.player_inactivity.live_timeout,
  ]);

  const handleListIconClick = (onSwitchProgramEndConvivaSession) => {
    setShowSideNav(!showSideNav);
    if (isLivePlayer) setCurrentIndex(0);
    restartUserInactivityTimer();
    if (!onSwitchProgramEndSessionRef.current) {
      onSwitchProgramEndSessionRef.current = { onSwitchProgramEndConvivaSession };
    }
  };

  /**
   * PlayerPage will reload more than once when user clicks play button,
   * thus use callback to trigger analytics calls
   */
  const triggerAnalytics = useCallback(() => {
    triggerAnalyticsCalls({
      isLivePlayer,
      programDetailsRef,
      programContent,
      isVodPlayer,
      convivaContentSubType,
      isRecordingPlayer,
      isMR,
      isLookbackStream,
      isRestartedStream,
      playbackType,
      appProvider,
      userProfile,
      isInHome,
      progressTime,
      trackPageView,
      resetIsPageViewTracked,
      isProgramChangedRef,
      isSwitchChannelRef,
      isInitMediaSessionTriggeredRef,
      isInitMediaSessionTriggeredTimer,
      isDynatraceTrackingEnabled,
    });
  }, [
    appProvider,
    convivaContentSubType,
    isLivePlayer,
    isMR,
    isRecordingPlayer,
    isVodPlayer,
    playbackType,
    programContent,
    progressTime,
    userProfile,
    trackPageView,
    resetIsPageViewTracked,
    isInHome,
    isLookbackStream,
    isDynatraceTrackingEnabled,
  ]);

  const onSeasonChange = (item) => {
    dispatch(loadCurrentSeasonDetails(appProvider, item));
    restartUserInactivityTimer();
    dispatch(loadCurrentSeasonEntitlements(appProvider, `${item?.id}?season=${item?.metadata?.season}`));
    if (parseInt(metadataInfo?.extendedMetadata?.season) !== item?.metadata?.season) {
      didSeasonChange.current = true;
    } else {
      didSeasonChange.current = false;
    }
  };

  const onEpisodeItemClick = useCallback(
    async (
      itemInfo,
      userCanPlay,
      isAutoplay = false,
      itemRowNumber = null,
      playNextEpisodeCallback = null,
      playNextBookmarkedEpisode = false,
      contentId,
      externalId
    ) => {
      const episodeContentId = contentId || itemInfo?.metadata?.contentId;
      const episodeExternalId = externalId || itemInfo?.metadata?.externalId;
      if (itemInfo && userCanPlay && isLoading.current === false && episodeContentId !== parseInt(currentEpisodeId)) {
        // Block changing episodes from the sidebar while stream is loading
        isLoading.current = true;
        useRoutePlaybackState.current = false;
        setCurrentEpisodeId(episodeContentId.toString());

        updateUrl(episodeContentId, episodeExternalId, isRecordingPlayer);
        setAnalyticsLink(itemRowNumber);
        loadProgramDetails(
          dispatch,
          appProvider,
          episodeContentId,
          isRecordingPlayer,
          contentType,
          loadRecordingProgramDetail,
          loadProgramDetail
        );

        isProgramChangedRef.current = true;
        handleProgramChange(
          playNextEpisodeCallback,
          onSwitchProgramEndSessionRef,
          trackMediaEnd,
          resetPlayerAttributes
        );
      }
      if (isAutoplay) {
        setBingeWatchEpisodeCounter((count) => count + 1);
      } else {
        restartUserInactivityTimer();
      }
    },
    [
      currentEpisodeId,
      setCurrentEpisodeId,
      isRecordingPlayer,
      appProvider,
      contentType,
      dispatch,
      setBingeWatchEpisodeCounter,
      restartUserInactivityTimer,
    ]
  );

  /* Live player merge specific functions */

  /**
   * Calls the heartbeat API to check if the current program is parental control blocked,
   * in which case an error is thrown.
   * @param {String|Number} channelId
   * @param {String|Number} assetId
   * @param {String} parentalPin
   * @returns {Promise}
   */
  const getHeartBeatEntitlements = useCallback(
    (channelId, assetId, parentalPin = null) => {
      return handleHeartBeat(appProvider, channelId, assetId, parentalPin, cancelTokenSource.current, setPCBlocked);
    },
    [appProvider]
  );

  const restartPcPinTimer = () => {
    if (pcPinTimer.current) {
      clearInterval(pcPinTimer.current);
    }
    pcPinTimer.current = setInterval(() => {
      pcPin.current = null;
    }, PLAYER_PIN_REFRESH_INTERVAL_MS);
  };

  /**
   * Shows the PIN modal for the user to enter their parental PIN to unlock the up next program.
   */
  const unlockOnNextProgram = useCallback(() => {
    const currentContentId = currentChannelId;
    const modalContent = createPinModal(
      currentContentId,
      assetId,
      getHeartBeatEntitlements,
      getMediaNodeMetaData,
      isConvivaAppTrackerEnabled,
      userProfile,
      appProvider,
      isInHome,
      setPlayerControlState,
      setShowContentLockedOverlay,
      pcPin,
      restartPcPinTimer,
      setIsPinLocked,
      translate,
      isDynatraceTrackingEnabled
    );

    reportParentalPinStart(assetId, contentType);
    setIsCallParentalPinLock(true);
    dispatch(showModalPopup(MODAL_TYPES.PIN, modalContent));
  }, [
    assetId,
    currentChannelId,
    getHeartBeatEntitlements,
    getMediaNodeMetaData,
    dispatch,
    translate,
    appProvider,
    isInHome,
    userProfile,
    isConvivaAppTrackerEnabled,
    contentType,
    isDynatraceTrackingEnabled,
  ]);

  const onLiveItemClick = (id, isContentOOHBlocked, itemRowNumber, programId) => {
    if (currentIndex === 0 && isLoading.current === false && id) {
      updateStreamReadyToChangeState(
        preventReadyToChangeStreams,
        isLookbackStream,
        id,
        currentChannelId,
        setAssetId,
        currentChannel,
        setIsReadyToChangeStreams
      );

      if (isContentOOHBlocked) {
        toastContentOOHBlocked(dispatch, outHomeIcon);
      } else if (id !== currentChannelId || liveProgramDetails?.id !== programId) {
        handleChannelChange(
          id,
          currentChannelId,
          useRoutePlaybackState,
          isRestartedStream,
          switchLive,
          isLookbackStream,
          exitLookbackMode,
          isLoading,
          dispatch,
          setShowContentLockedOverlay,
          setIsPinLocked,
          setCurrentChannelId,
          itemRowNumber,
          isSwitchChannelRef,
          onSwitchProgramEndSessionRef,
          resetPlayerAttributes,
          haltTransitionSOCU,
          isDynatraceTrackingEnabled
        );
      }
    }
    restartUserInactivityTimer();
  };

  /**
   * Triggers a retuning after purchase flow is complete
   */
  const purchaseCallback = () => {
    setPpvPurchasePackage(null);
    useRoutePlaybackState.current = false;
  };

  const { displayRecordingToast, recordingEventTypeRef } = useRecordingToast(isMR);

  const resetSetTeamRecordingResponse = useCallback(() => {
    dispatch(resetAction(SET_TEAM_RECORDINGS, "content"));
  }, [dispatch]);

  const scheduleTeamRecording = useCallback(
    async (teamRecordingsParams) => {
      if (teamRecordingsParams) {
        recordingEventTypeRef.current = ANALYTICS_EVENT_TYPES.VIDEO_RECORD_START;
        const sports = selectedAssetRecordingInfo?.assetToRecord?.metadata?.extendedMetadata?.dlum?.sports;
        const teamUmId = teamRecordingsParams?.filterBy?.teamUmId;
        const teamRecordingPayload = {
          teamSport: selectedAssetRecordingInfo?.assetToRecord?.metadata?.extendedMetadata?.seriesTitle || sports?.name,
        };
        if (teamUmId === sports?.homeUmId) {
          teamRecordingPayload.teamName = sports?.homeTeam;
        } else if (teamUmId === sports?.guestUmId) {
          teamRecordingPayload.teamName = sports?.guestTeam;
        }
        trackGenericAction(
          ANALYTICS_EVENT_TYPES.VIDEO_RECORD_START,
          {
            isTeamRecording: true,
            isItemLive: true,
            ...teamRecordingPayload,
            ...selectedAssetRecordingInfo?.assetToRecord,
            mappedContentType: MAPPED_CONTENT_TYPES.CPVR,
          },
          isDynatraceTrackingEnabled
        );
        recordingEventTypeRef.current = null;
        const response = await dispatch(
          setTeamRecordingAction(appProvider, { ...teamRecordingsParams, ...selectedAssetRecordingInfo?.assetToRecord })
        );
        if (response) resetSetTeamRecordingResponse();
      }
    },
    [
      appProvider,
      dispatch,
      resetSetTeamRecordingResponse,
      selectedAssetRecordingInfo,
      isDynatraceTrackingEnabled,
      recordingEventTypeRef,
    ]
  );

  const { recordingButtonClickHandler, resetRecordingActionValues, getRecordingModal } = useRecordingActions(
    appProvider,
    isMR,
    getMediaNodeMetaData,
    selectedAssetRecordingInfo,
    setSelectedAssetRecordingInfo,
    setShowRecordingModal,
    isRecordingActionInProgress,
    setRecordingActionError,
    recordingEventTypeRef
  );

  const { sortedRecordings, upNextRecording } = useRecordingSidebar(programContent, isRecordingPlayer);

  useRecordingEffects({
    isRecordingActionInProgress,
    isMR,
    setRecordingActionError,
    resetRecordingActionValues,
    selectedAssetRecordingInfo,
    appProvider,
    setSelectedAssetRecordingInfo,
    setShowRecordingModal,
    displayRecordingToast,
    recordingActionError,
    isTeamRecordingsEnabled,
  });

  const { updateSidePanelDetails, updateLiveProgramDetails, miniGuideChannels, onTabItemSelected } = useLiveSidebar(
    isLivePlayer,
    getSegmentTimestamp,
    setMiniGuideOnNowPrograms,
    setMiniGuideOnNextPrograms,
    getNextSegmentTimestamp,
    loadGuidePrograms,
    liveProgramCanUpdate,
    isLookbackStream,
    lookbackContent,
    currentChannel,
    updateLookbackStream,
    setLiveProgramDetails,
    miniGuideOnNowPrograms,
    currentChannelId,
    userProfile,
    history,
    setLivePlaybackCanStart,
    subscribedChannels,
    appProvider,
    setCurrentIndex,
    restartUserInactivityTimer,
    triggerSafariUpdate,
    setTriggerSafariUpdate,
    channelMapInfo
  );

  // Used to determine if the application is in partial panic for playback
  const passFeatureFlags = useCallback(() => {
    const modalContent = {
      title: translate("temporary_service_disruption"),
      history,
      isCloseable: true,
    };

    let message = "";

    if (isVODDisabled && isVodPlayer) {
      message = translate("panic_vod_playback_msg");
    } else if (isLIVEDisabled && isLivePlayer) {
      message = translate("panic_live_playback_msg");
    } else if (isCPVRDisabled && isRecordingPlayer) {
      message = translate("panic_cpvr_playback_msg");
    } else if (isRestartDisabled && restartLiveStream) {
      message = translate("panic_cpvr_playback_msg");
    } else if (isCatchUpDisabled && isLookbackStream) {
      message = translate("panic_catchup_playback_msg");
    } else if (isFASTDisabled && liveProgramDetails?.channel?.metadata?.extendedMetadata?.isFast === "Y") {
      message = translate("panic_fast_playback_msg");
    }

    if (message !== "") {
      modalContent.message = message;
      dispatch(showModalPopup(MODAL_TYPES.ERROR, modalContent));
      return false;
    }
    return true;
  }, [
    history,
    isCPVRDisabled,
    isCatchUpDisabled,
    isFASTDisabled,
    isLIVEDisabled,
    isLivePlayer,
    isLookbackStream,
    isRecordingPlayer,
    isRestartDisabled,
    isVODDisabled,
    isVodPlayer,
    restartLiveStream,
    dispatch,
    translate,
    liveProgramDetails,
  ]);

  /* Switches the player to PLTV mode */
  const switchLive = () => {
    if (useRoutePlaybackState.current) useRoutePlaybackState.current = false;
    isRestartedStream.current = false;
    restartAssetId.current = null;
    restartLiveStream && setRestartLiveStream(false);
    isLookbackStream && setIsLookbackStream(false);
  };
  /* Live player merge specific functions end */

  /** Side Effects begin */
  usePlayerUnmount(cancelTokenSource, userInactivityTimer, pcPinTimer);

  const { season, nextProgramInfo } = useStaticPlayerEffects(
    appProvider,
    contentType,
    isLivePlayer,
    isVodPlayer,
    isRecordingPlayer,
    stopDeltaTime,
    playbackID,
    isTVShow,
    programContent,
    userInactivityTimer,
    setShowStillWatchingPrompt,
    playbackType,
    onEpisodeItemClick,
    cancelTokenSource,
    bingeWatchEpisodeCounter,
    setBingeWatchEpisodeCounter,
    currentEpisodeId
  );

  useEffect(() => {
    if (appProvider && !channelMapInfo) dispatch(loadChannels(appProvider));
  }, [appProvider, channelMapInfo, dispatch]);

  useContentMetadata({
    isLivePlayer,
    programContent,
    setCurrentProgram,
    isRecordingPlayer,
    stopDeltaTime,
    currentEpisodeId,
    isVodPlayer,
    setMetadataInfo,
    setPageTitle,
    isProgramChangedRef,
    triggerAnalytics,
    appProvider,
    contentSubtype,
    programDetailsRef,
    liveProgramDetails,
    isRecordingEnabled,
    toastNotificationTimer,
    sportEventRecordingData,
  });

  useEffect(() => {
    setVideoStreamAttributes(streamInfo, isDynatraceTrackingEnabled);
  }, [streamInfo, isDynatraceTrackingEnabled]);

  useEffect(() => {
    // live player stream logic is separated for channel tune performance optimization, with the exception of PPV content
    if (isLivePlayer && !isPPV) return; // TODO: merge start streaming logic once the tune performance backend support is added to VOD/Recording playback, see TCDWC-1474

    /**
     * Fetch the video url and auth token for a given asset
     * @param {Number} assetId The asset ID
     * @param {String} parentalPin The pin that was entered by the user if the content was blocked by parental controls
     * @returns promise
     */
    const fetchStreamWrapper = (assetId, parentalPin, tokenOnly = false) => {
      return fetchStream({
        assetId,
        parentalPin,
        tokenOnly,
        currentAssetId,
        isLivePlayer,
        currentChannelId,
        currentEpisodeId,
        appProvider,
        contentType,
        cancelTokenSource,
        pcPin,
        pcPinTimer,
        restartPcPinTimer,
        getMediaNodeMetaData,
        isConvivaAppTrackerEnabled,
        userProfile,
        isInHome,
        setVideoUrl,
        showPlaybackError,
        setAuthToken,
        setHasChannelTuneInfo,
        setLivePlaybackCanStart,
        translate,
        history,
        showModalPopup,
        dispatch,
        setFatalPlaybackError,
        isDynatraceTrackingEnabled,
      });
    };
    handleStreamChange({
      playbackType,
      isReadyToChangeStreams,
      fatalPlaybackError,
      isLivePlayer,
      currentChannelId,
      currentEpisodeId,
      prevContentIdForApiRef,
      isPPV,
      ppvPurchasePackage,
      prevPurchasePackageForApiRef,
      useRoutePlaybackState,
      appProvider,
      contentType,
      cancelTokenSource,
      restartAssetId,
      setPpvPurchasePackage,
      setAssetId,
      setBookmark,
      setProgressTime,
      fetchStream: fetchStreamWrapper,
      showModalPopup,
      dispatch,
      getMediaNodeMetaData,
      translate,
      history,
      setIsCallParentalPinLock,
      pcPin,
      nextProgramInfo,
      isDynatraceTrackingEnabled,
    });
  }, [
    appProvider,
    contentType,
    currentEpisodeId,
    fatalPlaybackError,
    getMediaNodeMetaData,
    history,
    isLivePlayer,
    isReadyToChangeStreams,
    isVodPlayer,
    playbackType,
    dispatch,
    translate,
    updateLiveProgramDetails,
    isPPV,
    currentChannelId,
    ppvPurchasePackage,
    isInHome,
    programContent,
    userProfile,
    isConvivaAppTrackerEnabled,
    nextProgramInfo,
    isDynatraceTrackingEnabled,
  ]);

  usePlayerStreamInfo({
    isLivePlayer,
    videoUrl,
    authToken,
    appProvider,
    currentEpisodeId,
    isRecordingPlayer,
    externalId,
    initPlaybackTimestamp,
    passFeatureFlags,
    setStreamInfo,
    prevAuthToken,
    assetId,
    bookmark,
    history,
    redirectUri,
    miniGuideOnNowPrograms,
    currentChannelId,
    prevVideoUrl,
    setHasChannelTuneInfo,
    restartLiveStream,
    isRestartedStream,
    currentChannel,
    isInHome,
    isLookbackStream,
    streamInfo,
  });

  // The PlaybackError component exists in the VideoPlayer, so we need to set the stream info
  // so the VideoPlayer renders to show the playback error
  useEffect(() => {
    if (fatalPlaybackError) {
      initPlaybackTimestamp.current = null; // Reset initial playback timestamp on fatal error
      setStreamInfo({});
      if (!isLivePlayer) setMetadataInfo({});
    }
  }, [fatalPlaybackError, isLivePlayer]);

  useEffect(() => {
    const shouldExecute = isLivePlayer
      ? isCallParentalPinLock && liveProgramDetails
      : isCallParentalPinLock && programContent && playbackType;
    if (shouldExecute) {
      setIsCallParentalPinLock(false);
      trackGenericAction(
        ANALYTICS_EVENT_TYPES.PARENTAL_PIN_CONTENT_LOCK,
        getMediaNodeMetaData(),
        isDynatraceTrackingEnabled
      );
      if (isConvivaAppTrackerEnabled) {
        trackConvivaCustomEvent(
          ANALYTICS_EVENT_TYPES.PARENTAL_PIN_CONTENT_LOCK,
          getCustomContextMetadata(getMediaNodeMetaData(), userProfile, appProvider, isInHome)
        );
      }
    }
  }, [
    isLivePlayer,
    isCallParentalPinLock,
    liveProgramDetails,
    getMediaNodeMetaData,
    programContent,
    playbackType,
    appProvider,
    isInHome,
    userProfile,
    isConvivaAppTrackerEnabled,
    isDynatraceTrackingEnabled,
  ]);

  useEffect(() => {
    if ((!isLivePlayer || (isLivePlayer && isPPV)) && isRented) {
      dispatch(showToastNotification(translate("purchase_success_notification")));
    }
  }, [isLivePlayer, isPPV, isRented, dispatch, translate]);

  /** Live specific side effects */

  useLivePlayerEffects({
    isLivePlayer,
    currentChannelId,
    assetId,
    resetRecordingActionValues,
    restartUserInactivityTimer,
    toastNotificationTimer,
    isInitMediaSessionTriggeredTimer,
    loadGuidePrograms,
    getSegmentTimestamp,
    getNextSegmentTimestamp,
    userProfile,
    appProvider,
    subscribedChannels,
    cancelTokenSource,
    isUserProfilesEnabled,
    isPcPinFeatureEnabled,
    isParentalPinEnabled,
    setIsParentalPinEnabled,
    liveProgramDetails,
    programDetailsRef,
    isSwitchChannelRef,
    triggerAnalytics,
    isInitMediaSessionTriggeredRef,
    isInHome,
    getHeartBeatEntitlements,
    setPCBlocked,
    setShowSideNav,
    setShowContentLockedOverlay,
  });

  useLiveChannelTune({
    isLivePlayer,
    useRoutePlaybackState,
    playbackAuthToken,
    appProvider,
    currentChannel,
    isPPV,
    exitLookbackMode,
    setAssetId,
    restartLiveStream,
    isCTTEnabled,
    isLookbackStream,
    setVideoUrl,
    setLivePlaybackCanStart,
    contentType,
    cancelTokenSource,
    setAuthToken,
    assetId,
    currentChannelId,
    pcPin,
    pcPinTimer,
    restartPcPinTimer,
    getMediaNodeMetaData,
    isConvivaAppTrackerEnabled,
    userProfile,
    isInHome,
    history,
    setFatalPlaybackError,
    setIsCallParentalPinLock,
    updateLiveProgramDetails,
    fatalPlaybackError,
    restartAssetId,
    programContent,
    isReadyToChangeStreams,
    hasChannelTuneInfo,
    setHasChannelTuneInfo,
    isDynatraceTrackingEnabled,
  });

  const { triggerLookback, fetchNextLiveStream, triggerRestart, prevLiveProgramDetails } = useSOCUEffects({
    isLookbackStream,
    lookbackContent,
    history,
    restartAssetId,
    getMediaNodeMetaData,
    isConvivaAppTrackerEnabled,
    userProfile,
    appProvider,
    isInHome,
    updateLookbackStream,
    isRestartedStream,
    setRestartLiveStream,
    liveProgramCanUpdate,
    setIsLookbackStream,
    setUpdateLookbackStream,
    currentChannel,
    cancelTokenSource,
    channelID,
    useRoutePlaybackState,
    restartLiveStream,
  });

  useEffect(() => {
    if (useRoutePlaybackState.current && liveProgramDetails?.metadata && !livePlaybackCanStart) {
      setLivePlaybackCanStart(true);
    }
  }, [liveProgramDetails, livePlaybackCanStart]);

  /** Live specific side effects end*/

  /** Side Effects end */

  const closeTeamsRecordingInstancesModal = useCallback(() => {
    setShowRecordingInstancesModal(false);
    setTeamRecordingData(null);
  }, []);

  const initiatePlayback = useCallback(
    (contentId, externalId) => {
      closeTeamsRecordingInstancesModal();
      const { episodeInfo, canPlay, index } = teamRecordingData;
      onEpisodeItemClick(episodeInfo, canPlay, false, index, null, true, contentId, externalId);
    },
    [closeTeamsRecordingInstancesModal, onEpisodeItemClick, teamRecordingData]
  );

  const showRecordedEpisodeInstancesModal = useCallback((recordedEpisodeData) => {
    setShowRecordingInstancesModal(true);
    setTeamRecordingData(recordedEpisodeData);
  }, []);

  const recordingSeriesId = currentProgram?.metadata?.recordingSeriesId;
  const hideListChannel = useCallback(() => {
    if ((isSportRecording || isTeamRecording) && recordingSeriesId) {
      return false;
    }
    return sortedRecordings?.length === 1;
  }, [isSportRecording, isTeamRecording, sortedRecordings, recordingSeriesId]);

  /** Conditional rendering  */
  const renderPlayer = () => {
    const nextProgramInfoProp = isVodPlayer
      ? nextProgramInfo
      : isRecordingPlayer
      ? {
          nextProgram: upNextRecording,
          nextEpisodePoster:
            upNextRecording && getAVSKeyArtImage(upNextRecording.metadata, IMAGES.ASPECT_RATIOS.DIM_9x16),
          loadNextProgram: (isAutoplay = false, playNextEpisodeCallback) =>
            onEpisodeItemClick(upNextRecording, true, isAutoplay, null, playNextEpisodeCallback),
        }
      : { nextProgram: null, nextEpisodePoster: null, loadNextProgram: null };
    const shouldRenderPlayer =
      streamInfo &&
      metadataInfo &&
      (isVodPlayer ||
        isRecordingPlayer ||
        (isLivePlayer && livePlaybackCanStart) ||
        (currentChannel?.metadata?.extendedMetadata?.isAllowUnrestrictedPlay === "Y" &&
          currentChannel?.metadata?.extendedMetadata?.isFast === "Y"));
    const playerComponents = {
      LIVE: LivePlayer,
      VOD: VodPlayer,
      RECORDING: RecordingPlayer,
    };
    const PlayerComponent = playerComponents[contentType];
    return (
      shouldRenderPlayer && (
        <PlayerComponent
          className={`video-player ${showSideNav ? "shrink" : ""}`}
          type={isVodPlayer ? VOD : isLivePlayer ? LIVE : RECORDING}
          contentType={contentType}
          streamInfo={streamInfo}
          handleListIconClick={handleListIconClick}
          buttonRef={!isLivePlayer ? buttonRef : null}
          metadataInfo={metadataInfo}
          control={playerControlState}
          nextProgramInfo={nextProgramInfoProp}
          onPlaybackStateChange={(playbackState) =>
            handlePlaybackStateChange(
              playbackState,
              isLivePlayer,
              updateSidePanelDetails,
              moment,
              playerErrorCodes,
              nonFatalErrorCodes,
              showToastNotification,
              showPlaybackError,
              playbackError,
              dispatch
            )
          }
          onFullScreenClick={(isFullScreen) =>
            onFullScreenClick(isFullScreen, setIsInFullScreen, setShowSideNav, showSideNav)
          }
          showListButton={
            isRecordingPlayer && (isTeamRecording || isSportRecording)
              ? false
              : isLivePlayer || isTVShow.current
              ? true
              : false
          }
          resumeTimeSeconds={!isLivePlayer && playbackType !== PLAYBACK_TYPES.TRAILER ? progressTime : null}
          isSideNavOpen={showSideNav}
          currentContentId={isLivePlayer ? currentChannelId : currentEpisodeId}
          isReadyCallback={() => resetIsLoading(isLoading)}
          playbackType={!isLivePlayer ? playbackType : null}
          showStillWatchingPrompt={showStillWatchingPrompt}
          restartUserInactivityTimer={restartUserInactivityTimer}
          updateVodProgress={isVodPlayer || (isRecordingPlayer && sortedRecordings?.length > 1) ? setVodProgress : null}
          setIsReadyToChangeStreams={setIsReadyToChangeStreams}
          hideListChannel={hideListChannel()}
          getMediaNodeMetaData={getMediaNodeMetaData}
          triggerAnalyticsCalls={triggerAnalytics}
          isProgramChangedRef={
            isVodPlayer || (isRecordingPlayer && sortedRecordings?.length > 1) ? isProgramChangedRef : null
          }
          mappedContentType={isRecordingPlayer ? (isMR ? MAPPED_CONTENT_TYPES.LPVR : MAPPED_CONTENT_TYPES.CPVR) : null}
          liveProgramDetails={isLivePlayer ? liveProgramDetails : null}
          recordingButtonClickHandler={isLivePlayer ? recordingButtonClickHandler : null}
          programDetailsRef={programDetailsRef}
          isMR={isLivePlayer ? isMR : null}
          bookmark={bookmark}
          contentLockedOverlay={
            isLivePlayer && showContentLockedOverlay ? (
              <ContentLockedOverlay
                isSideNavOpen={showSideNav}
                isPinLocked={isPinLocked}
                unlockContentHandler={unlockOnNextProgram}
              />
            ) : null
          }
          livePlaybackCanStart={livePlaybackCanStart}
          liveProgramCanUpdate={liveProgramCanUpdate}
          triggerRestart={triggerRestart}
          isRestartedStream={restartLiveStream}
          switchLive={switchLive}
          isReadyToChangeStreams={isReadyToChangeStreams}
          transitionProgram={(isNextProgramRestartable) =>
            transitionProgram(isNextProgramRestartable, isNextProgramNonRestartable, switchLive)
          }
          isNextProgramNonRestartable={isNextProgramNonRestartable.current}
          isLookbackStream={isLookbackStream}
          resetPlayerPage={() =>
            resetPlayerPage(
              prevVideoUrl,
              isLoading,
              setHasChannelTuneInfo,
              setIsReadyToChangeStreams,
              preventReadyToChangeStreams
            )
          }
          exitLookbackMode={exitLookbackMode.current}
          changeLookbackStream={(lookbackProgramDetails) =>
            changeLookbackStream(lookbackProgramDetails, restartAssetId, useRoutePlaybackState, setHasChannelTuneInfo)
          }
          ppvPurchasePackage={ppvPurchasePackage}
          purchaseCallback={purchaseCallback}
          updateSeasonEntitlements={() =>
            updateSeasonEntitlements(
              appProvider,
              currentProgram,
              loadCurrentSeasonEntitlements,
              didSeasonChange,
              dispatch
            )
          }
          initPlaybackTimestamp={initPlaybackTimestamp}
          dtContentType={dtContentType}
          convivaContentSubType={convivaContentSubType}
          isPlayerControlsDisable={isPlayerControlsDisable}
          PCBlocked={PCBlocked}
          parentalPin={pcPin.current}
          fetchNextLiveStream={fetchNextLiveStream}
          triggerLookback={triggerLookback}
          exitLookback={() => exitLookback(exitLookbackMode)}
          updatePrevLiveProgramDetails={(liveProgramDetails, isLookbackStream, isRestartedStream) =>
            updatePrevLiveProgramDetails(
              prevLiveProgramDetails,
              liveProgramDetails,
              isLookbackStream,
              isRestartedStream
            )
          }
          prevLiveProgramDetails={prevLiveProgramDetails.current}
          currentPlayingSource={currentPlayingSource}
          setTriggerSafariUpdate={setTriggerSafariUpdate}
          userProfile={userProfile}
          haltTransitionSOCU={haltTransitionSOCU}
          isUnrestrictedPlay={isUnrestrictedPlay}
          errCode={playbackContent?.errCode}
          isTeamRecording={isTeamRecording}
        />
      )
    );
  };

  const renderSideBar =
    isTVShow.current &&
    ((isVodPlayer && seasonFeedContent) ||
      (isRecordingPlayer && (isSportRecording || isTeamRecording || sortedRecordings?.length > 1))) ? (
      showSideNav ? (
        <div>
          <VodPlayerSideBar
            onSeasonChange={onSeasonChange}
            onEpisodeItemClick={onEpisodeItemClick}
            currentEpisodeId={currentEpisodeId}
            handleHideButtonClick={handleListIconClick}
            currentProgram={currentProgram}
            currentSeason={season}
            vodProgress={vodProgress}
            recordingsToDisplay={sortedRecordings}
            isRecordingPlayer={isRecordingPlayer}
            isCPVRBookmarksEnabled={isCPVRBookmarksEnabled}
            showRecordedEpisodeInstancesModal={showRecordedEpisodeInstancesModal}
            sportEventRecordingData={sportEventRecordingData}
          />
        </div>
      ) : null
    ) : isVodPlayer ? (
      showSideNav ? (
        <MovieSideBar handleHideButtonClick={handleListIconClick} content={currentProgram} />
      ) : null
    ) : isLivePlayer && miniGuideOnNowPrograms ? (
      <div>
        <VideoPlayerSideBar
          channels={miniGuideChannels}
          programs={currentIndex === 0 ? miniGuideOnNowPrograms : miniGuideOnNextPrograms}
          currentPlayingProgram={liveProgramDetails}
          currentChannelId={currentChannelId}
          onLiveItemClick={onLiveItemClick}
          handleHideButtonClick={handleListIconClick}
          isInFullScreen={isInFullScreen}
          onTabItemSelected={onTabItemSelected}
          onProgressComplete={updateSidePanelDetails}
          recordingButtonClickHandler={recordingButtonClickHandler}
          isMR={isMR}
          isPlayerControlsDisable={isPlayerControlsDisable}
          isLoggedIn={userProfile?.isLoggedIn}
          type={isVodPlayer ? VOD : isLivePlayer ? LIVE : RECORDING}
          isVisible={showSideNav} // Controls the shared currentTime timer — pauses when sidebar is hidden
        />
      </div>
    ) : null;

  const renderRecordingModal =
    isLivePlayer &&
    showRecordingModal &&
    selectedAssetRecordingInfo &&
    getRecordingModal(isSportEvent(selectedAssetRecordingInfo), scheduleTeamRecording);

  return (
    <>
      <SeoPageTags title={pageTitle} keywords={["optik", "telus"]} />
      <div className="player-with-sidebar">
        {renderPlayer()}
        <div className={!showSideNav ? "video-sidebar closed" : "video-sidebar"}>{renderSideBar}</div>
      </div>
      {renderRecordingModal}
      {showRecordingInstancesModal && teamRecordingData?.program && (
        <TeamsRecordingInstancesModal
          closeTeamsRecordingInstancesModal={closeTeamsRecordingInstancesModal}
          appProvider={appProvider}
          recordingResponse={teamRecordingData.program}
          initiatePlayback={initiatePlayback}
        />
      )}
    </>
  );
}

export default PlayerPage;
